import {
  Component,
  Input,
  OnInit,
  booleanAttribute,
  model,
  ChangeDetectionStrategy,
} from '@angular/core';

/**
 * Collapsible group. Open state is keyed by `string[]` (single mode holds 0 or 1 entries),
 * regardless of `[multiple]`. Project `<sui-accordion-item>`s as content; each calls back into
 * this parent (injected) to toggle/read its open state.
 *
 * **Controlled or uncontrolled** — `value` is a `model()`:
 *  - *Uncontrolled* (don't bind `value`): the accordion keeps its own memory; use `[initialOpen]`
 *    to start some panels open, and listen to `(valueChange)` if you care.
 *  - *Controlled*: `[(value)]`, or `[value]` + `(valueChange)`, and you own the state.
 *
 * For a **dynamic/streaming item list**, controlled is usually right — *you* reconcile `value` as
 * items come/go (keep user collapses, default new items open); there's no built-in memory of that
 * (see `filters.component`'s `reconcileAccordionOpenState`).
 *
 * Self-contained (not on ng-primitives): the accordion's only real behavior is open-state + ARIA,
 * and native `<button>` headers cover the keyboard contract — so wrapping a primitive bought only
 * DI friction. See CRAFTING-GUIDE §11.
 */
@Component({
  selector: 'sui-accordion',
  standalone: true,
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sui-accordion.component.scss',
})
export class SuiAccordionComponent implements OnInit {
  /** Allow multiple items open at once. Default: single (opening one closes the others). */
  @Input({ transform: booleanAttribute }) multiple = false;
  /** Single mode only: whether re-clicking the open item collapses it. */
  @Input({ transform: booleanAttribute }) collapsible = true;
  @Input({ transform: booleanAttribute }) disabled = false;

  /**
   * Open item keys (single mode: 0 or 1 entries). A `model()` — works bound (`[(value)]`) or
   * unbound (internal memory). Don't one-way-bind a fresh array literal/`.map(...)` here: it
   * re-applies every change-detection pass and clobbers the user's toggles — use `[initialOpen]`
   * for an uncontrolled seed, or two-way `[(value)]` to control it.
   */
  readonly value = model<string[]>([]);

  /** Uncontrolled seed: keys open on first render. Applied once, only if `value` is still empty. */
  @Input() initialOpen?: string[];

  ngOnInit(): void {
    if (this.initialOpen?.length && this.value().length === 0) {
      this.value.set(this.initialOpen);
    }
  }

  isOpen(key: string): boolean {
    return this.value().includes(key);
  }

  toggle(key: string): void {
    if (this.disabled) {
      return;
    }
    const open = this.value();
    const currentlyOpen = open.includes(key);

    let next: string[];
    if (this.multiple) {
      next = currentlyOpen ? open.filter((k) => k !== key) : [...open, key];
    } else if (currentlyOpen) {
      next = this.collapsible ? [] : open; // non-collapsible re-click = no-op
    } else {
      next = [key];
    }

    if (next === open) {
      return;
    }
    this.value.set(next); // model → emits (valueChange) for controlled consumers
  }
}
